import { z } from 'zod';
/** PointFive Score v1.4 reference inputs — the §16 allocation table, modifier, ranges, scope accounts, tiers, laid over
 *  the design reference's own layout (script lines 113–181, 1462–1469, 1711–1715).
 *  The engine (apps/web/src/model/pfEngine.ts) is deterministic over these inputs; the UI never recalculates scores. */
export const PfMetricSchema = z.tuple([
  z.string(), // name
  z.number(), // max points
  z.string(), // raw value (display)
  z.string(), // adjusted value (display)
  z.enum(['higher', 'lower']), // better direction
  // 0 measured · 1 data-limited · 2 input-missing. v1.4 §4.5 separates "you have not traded enough yet" from "your
  // broker never sent this field": the first costs 2pp of confidence and the trader can fix it, the second costs 1pp
  // and they cannot. A single boolean would have told half of them to do something impossible.
  z.union([z.literal(0), z.literal(1), z.literal(2)]),
  z.string(), // description
  z.string(), // scoring explanation
]);
export const PfPillarSpecSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    max: z.number(),
    base: z.number(),
    metrics: z.array(PfMetricSchema),
  })
  .strict();
export const PfRangeSchema = z
  .object({ res: z.string(), n: z.number(), labels: z.array(z.string()), spread: z.number() })
  .strict();
export const PfTierRowSchema = z
  .object({
    name: z.string(),
    min: z.number(),
    max: z.number(),
    tone: z.string(),
    ink: z.string(),
    pct: z.string(),
    label: z.string(),
  })
  .strict();
/** Investor viewer row on the score card: initials, name, offer line, status badge (label + colour tokens), actionable flag. */
export const PfInvestorSchema = z
  .object({
    initials: z.string(),
    name: z.string(),
    offer: z.string(),
    badge: z.string(),
    badgeFg: z.string(),
    badgeBg: z.string(),
    act: z.boolean(),
  })
  .strict();
export const ScoreDataSchema = z
  .object({
    versions: z.object({ score_version: z.string(), calibration_version: z.string() }).strict(),
    tiers: z.array(z.tuple([z.number(), z.string()])),
    spec: z.array(PfPillarSpecSchema),
    modifier: z
      .object({
        synergies: z.array(z.tuple([z.string(), z.number()])),
        penalties: z.array(z.tuple([z.string(), z.number()])),
      })
      .strict(),
    ranges: z.record(z.string(), PfRangeSchema),
    /** scope id → [label, colour token, score multiplier, eligible trades, months, confidence base, net P&L, win rate %] */
    accounts: z.record(
      z.string(),
      z.tuple([
        z.string(),
        z.string(),
        z.number(),
        z.number(),
        z.number(),
        z.number(),
        z.number(),
        z.number(),
      ]),
    ),
    months: z.array(z.string()),
    tierTable: z.array(PfTierRowSchema),
    /** Investor interests shown on the PointFive Score screen (pfModel(), script lines 1711–1715). */
    investors: z.array(PfInvestorSchema),
    /** Public-card identity of the scored trader: display name and public slug. */
    profile: z.object({ name: z.string(), slug: z.string() }).strict(),
    /** Leaderboard population the rank ("#n / 4,128") is computed against. */
    leaderboard: z.object({ population: z.number() }).strict(),
  })
  .strict();
export type ScoreData = z.infer<typeof ScoreDataSchema>;
export type PfMetricSpec = z.infer<typeof PfMetricSchema>;
export type PfPillarSpec = z.infer<typeof PfPillarSpecSchema>;
export type PfRange = z.infer<typeof PfRangeSchema>;
export type PfInvestor = z.infer<typeof PfInvestorSchema>;
